/**
 * Dynamic imports that remember their module, and views that render without
 * suspending.
 *
 * A view that suspends shows its Suspense fallback, and React then holds the
 * real view back until its 300 ms fallback throttle passes, even when the code
 * arrives 50 ms later. A view over a cached module renders at once when its
 * module is in, and otherwise shows its own placeholder until the import
 * resolves, so no Suspense boundary is involved.
 */
import {
  createContext,
  createElement,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
  type ComponentType,
  type ReactNode,
} from 'react';

export interface CachedModuleLoader<Module> {
  (): Promise<Module>;
  /** The module, once an earlier call loaded it. */
  readonly loaded: () => Module | undefined;
}

const cachedLoaders = new WeakSet<object>();

function isCachedLoader<Module>(
  importer: () => Promise<Module>
): importer is CachedModuleLoader<Module> {
  return cachedLoaders.has(importer);
}

export function cachedModule<Module>(importer: () => Promise<Module>): CachedModuleLoader<Module> {
  if (isCachedLoader(importer)) return importer;
  let module: Module | undefined;
  let pending: Promise<Module> | undefined;
  const load = () => {
    pending ??= importer().then(
      value => {
        module = value;
        return value;
      },
      error => {
        // A failed import may succeed on a later attempt.
        pending = undefined;
        throw error;
      }
    );
    return pending;
  };
  const loader = Object.assign(load, { loaded: () => module });
  cachedLoaders.add(loader);
  return loader;
}

/** What a view over a cached module shows and reports while its code loads. */
export interface CachedViewWaiting {
  readonly placeholder: ReactNode;
  /** True while a view waits for its module, false once it renders or unmounts. */
  readonly onWaiting: (waiting: boolean) => void;
}

export const CachedViewWaitingContext = createContext<CachedViewWaiting>({
  placeholder: null,
  onWaiting: () => undefined,
});

const ignoreWaiting = () => undefined;

/**
 * Gives the cached views inside it their own placeholder, for a pane whose
 * code may still load after the screen around it. Their waiting reports to the
 * enclosing views; with `reportWaiting` false it does not, for code that a
 * user action loads (a compose window) and that no screen waits for.
 */
export function CachedViewPlaceholder({
  placeholder,
  reportWaiting = true,
  children,
}: {
  placeholder: ReactNode;
  reportWaiting?: boolean;
  children: ReactNode;
}) {
  const { onWaiting } = useContext(CachedViewWaitingContext);
  const value = useMemo<CachedViewWaiting>(
    () => ({ placeholder, onWaiting: reportWaiting ? onWaiting : ignoreWaiting }),
    [placeholder, reportWaiting, onWaiting]
  );
  return createElement(CachedViewWaitingContext.Provider, { value }, children);
}

/**
 * A view over a cached module. `load` may wrap the loader, for example with
 * retries; a failed import throws to the nearest error boundary.
 */
export function viewFromCachedModule<Module, Props extends object>(
  loader: CachedModuleLoader<Module>,
  pick: (module: Module) => ComponentType<Props>,
  load: () => Promise<Module> = loader
): ComponentType<Props> {
  function CachedModuleView(props: Props) {
    const [module, setModule] = useState<Module | undefined>(loader.loaded);
    const [failure, setFailure] = useState<{ error: unknown }>();
    const { placeholder, onWaiting } = useContext(CachedViewWaitingContext);
    const waiting = module === undefined;

    useLayoutEffect(() => {
      if (!waiting) return;
      let live = true;
      onWaiting(true);
      load().then(
        value => {
          if (live) setModule(() => value);
        },
        (error: unknown) => {
          if (live) setFailure({ error });
        }
      );
      return () => {
        live = false;
        onWaiting(false);
      };
    }, [waiting, onWaiting]);

    if (failure) throw failure.error;
    if (module === undefined) return placeholder;
    return createElement(pick(module), props);
  }
  return CachedModuleView;
}
